import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ROUTES } from '@/constants/routes';
import { authStore } from '@/stores/AuthStore';
import AppLayout from '@/components/Layout/AppLayout';
import { MobileLayout } from '@/components/Layout';
import { PlatformRoute } from './PlatformRoute';
import { usePlatform } from '@/hooks';
import { 
  Dashboard, 
  NotFound, 
  AuthPhonePage,
  MobileAuthPhonePage,
  AuthCodePage,
  MobileAuthCodePage,
  AuthImpersonatePage,
  OrdersPage,
  MobileOrdersPage,
  AnalyticsPage,
  MobileAnalyticsPage,
  SchedulePage,
  MobileSchedulePage,
  SettingsPage,
  PaymentMockPage,
  MobileNotificationsPage,
  MobileOrderDetailsPage
  ,
  ScheduleEditPage,
  EmployeesPage,
  EmployeeEditPage,
  ShiftEditPage,
  ShiftTemplateEditPage,
  PaymentSchemeTemplateEditPage
} from '@/pages';
import { MobileCreateBooking } from '@/mobile-components';
import ServicesPageWrapper from '@/pages/ServicesPage';

/**
 * AuthGuard - единый слой защиты авторизации для всех защищённых маршрутов.
 * Оборачивает ВСЕ защищённые роуты (и mobile, и desktop), гарантируя что
 * ни один маршрут внутри не может быть доступен без авторизации.
 */
const AuthGuard = observer(() => {
  if (!authStore.isAuthenticated) {
    return <Navigate to={ROUTES.AUTH_PHONE} replace />;
  }
  return <Outlet />;
});

/**
 * ProtectedLayout - Layout wrapper для защищенных маршрутов
 * Выбирает между MobileLayout и AppLayout в зависимости от платформы
 * Проверяет авторизацию и редиректит на страницу входа при необходимости
 */
const ProtectedLayout = observer(() => {
  const platform = usePlatform();

  if (!authStore.isAuthenticated) {
    return <Navigate to={ROUTES.AUTH_PHONE} replace />;
  }

  // MobileLayout и AppLayout оба используют Outlet для рендера дочерних маршрутов
  return platform === 'mobile' ? <MobileLayout /> : <AppLayout><div /></AppLayout>;
});

/**
 * PrivateRoute - обертка для отдельных защищенных страниц без layout
 * Используется для desktop версии, где AppLayout принимает children
 */
const PrivateRoute = observer(({ children }: { children: React.ReactNode }) => {
  const platform = usePlatform();

  if (!authStore.isAuthenticated) {
    return <Navigate to={ROUTES.AUTH_PHONE} replace />;
  }

  return platform === 'mobile' ? <>{children}</> : <AppLayout>{children}</AppLayout>;
});

const AppRouter = observer(() => {
  const platform = usePlatform();
  
  return (
    <Routes>
      {/* Редирект с корня на /orders */}
      <Route path="/" element={<Navigate to={ROUTES.ORDERS} replace />} />
      
      {/* Публичные маршруты авторизации (без MainLayout) */}
      <Route 
        path={ROUTES.AUTH_PHONE} 
        element={
          <PlatformRoute 
            desktop={AuthPhonePage} 
            mobile={MobileAuthPhonePage}
          />
        } 
      />
      <Route 
        path={ROUTES.AUTH_CODE} 
        element={
          <PlatformRoute 
            desktop={AuthCodePage} 
            mobile={MobileAuthCodePage}
          />
        } 
      />
      <Route
        path={ROUTES.AUTH_IMPERSONATE}
        element={<AuthImpersonatePage />}
      />
      
      {/* Публичный маршрут для мок-платежа (без авторизации и без Layout) */}
      <Route path={ROUTES.PAYMENT_MOCK} element={<PaymentMockPage />} />

      {/* Защищенные маршруты - AuthGuard проверяет авторизацию для ВСЕХ вложенных роутов */}
      <Route element={<AuthGuard />}>
      {platform === 'mobile' ? (
        <>
          <Route element={<ProtectedLayout />}>
            <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
            <Route path={ROUTES.SERVICES} element={<ServicesPageWrapper />} />
            <Route path={ROUTES.ORDERS} element={<MobileOrdersPage />} />
            <Route 
              path={ROUTES.ANALYTICS} 
              element={
                <PlatformRoute 
                  desktop={AnalyticsPage} 
                  mobile={MobileAnalyticsPage}
                />
              } 
            />
            <Route 
              path={ROUTES.SCHEDULE} 
              element={
                <PlatformRoute 
                  desktop={SchedulePage} 
                  mobile={MobileSchedulePage}
                />
              } 
            />
            <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
          </Route>
          {/* Страница создания заказа без layout */}
          <Route path="/orders/create" element={<PrivateRoute><MobileCreateBooking /></PrivateRoute>} />
          {/* Страница детального просмотра заказа без layout */}
          <Route path="/orders/:uuid" element={<PrivateRoute><MobileOrderDetailsPage /></PrivateRoute>} />
          {/* Страница уведомлений */}
          <Route path={ROUTES.NOTIFICATIONS} element={<PrivateRoute><MobileNotificationsPage /></PrivateRoute>} />
        </>
      ) : (
        <>
          <Route path={ROUTES.DASHBOARD} element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path={ROUTES.SERVICES} element={<PrivateRoute><ServicesPageWrapper /></PrivateRoute>} />
          <Route path={ROUTES.EMPLOYEES} element={<PrivateRoute><EmployeesPage /></PrivateRoute>} />
          <Route path={ROUTES.EMPLOYEE_NEW} element={<PrivateRoute><EmployeeEditPage /></PrivateRoute>} />
          <Route path={ROUTES.EMPLOYEE_EDIT} element={<PrivateRoute><EmployeeEditPage /></PrivateRoute>} />
          <Route path={ROUTES.SHIFT_EDIT} element={<PrivateRoute><ShiftEditPage /></PrivateRoute>} />
          <Route
            path={ROUTES.PAYMENT_SCHEME_TEMPLATE_NEW}
            element={<PrivateRoute><PaymentSchemeTemplateEditPage /></PrivateRoute>}
          />
          <Route
            path={ROUTES.PAYMENT_SCHEME_TEMPLATE_EDIT}
            element={<PrivateRoute><PaymentSchemeTemplateEditPage /></PrivateRoute>}
          />
          <Route
            path={ROUTES.SHIFT_TEMPLATE_NEW}
            element={<PrivateRoute><ShiftTemplateEditPage /></PrivateRoute>}
          />
          <Route
            path={ROUTES.SHIFT_TEMPLATE_EDIT}
            element={<PrivateRoute><ShiftTemplateEditPage /></PrivateRoute>}
          />
          <Route path={ROUTES.ORDERS} element={<PrivateRoute><OrdersPage /></PrivateRoute>} />
          <Route path={ROUTES.ANALYTICS} element={<PrivateRoute><AnalyticsPage /></PrivateRoute>} />
          <Route 
            path={ROUTES.SCHEDULE} 
            element={
              <PrivateRoute>
                <PlatformRoute 
                  desktop={SchedulePage} 
                  mobile={MobileSchedulePage}
                />
              </PrivateRoute>
            } 
          />
          <Route 
            path={ROUTES.SCHEDULE_EDIT}
            element={
              <PrivateRoute>
                <PlatformRoute 
                  desktop={ScheduleEditPage}
                  mobile={MobileSchedulePage}
                />
              </PrivateRoute>
            }
          />
          <Route path={ROUTES.SETTINGS} element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
          {/* Редирект на главную для desktop */}
          <Route path={ROUTES.NOTIFICATIONS} element={<Navigate to={ROUTES.ORDERS} replace />} />
        </>
      )}
      </Route>

      {/* 404 страница */}
      <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
      
      {/* Перенаправление всех неизвестных маршрутов на 404 */}
      <Route path="*" element={<Navigate to={ROUTES.NOT_FOUND} replace />} />
    </Routes>
  );
});

AppRouter.displayName = 'AppRouter';

export default AppRouter;